import { type Citadel, FILE_PARENT_SUBJECTS, fileWriteIsDeniedFor } from "@msuite/citadel";
import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu, withoutDanglingDividers } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { RowActionsWrapper } from "@msuite/ui/table";
import { Fragment, responseHandler } from "@msuite/ui/utils";
import { isOfferImportFileName } from "@msuite/utils/files";
import type { Enums, FileClassificationCandidate, Tables } from "@msuite/utils/types";
import { fileInboxEntry } from "@/api/files";
import { client } from "@/hono";
import { useCitadel } from "@/hooks/use-citadel";
import { useFileCapabilities } from "@/hooks/use-file-capabilities";
import { ImportOfferFilesModal } from "@/modals/calculations/import-offer-files-modal";
import { FileFilingModal } from "@/modals/files/file-filing-modal";

/** How many of the classifier's guesses the menu offers before the picker takes over. */
const OFFERED_CANDIDATES = 2;

/** Props Interface */
interface FileRowActionsProps {
	/**
	 * The whole `files` row rather than its id, because an unfiled file hangs on nothing and is
	 * therefore its uploader's and the administrator's — `createdBy` is what that is asked of.
	 */
	file: Tables<"files">;
	/** What the classifier had to choose between, offered ahead of the picker. */
	candidates: FileClassificationCandidate[];
	/** What the classifier read the document as, or null when it never got that far. */
	documentKind: Enums<"documentKind"> | null;
	/**
	 * Called once an action has taken the user to another page. The inbox passes nothing — a route
	 * change unmounts the page and the menu with it — but the batch report is mounted above the
	 * routes and would otherwise stay on top of wherever the offer import landed.
	 */
	onNavigate?: () => void;
}

/**
 * What can be done with one unfiled file: read it into a calculation, take the classifier's guess,
 * pick somewhere else, or throw it away.
 *
 * **The calculation stands above the guesses**, and only for an Angebot. Every other entry in this
 * menu answers "which record does this belong to"; that one answers a different question, and for
 * a dropped Leistungsverzeichnis it is the only question with an answer — a GAEB file names no
 * site, so the guesses below it are empty by construction.
 *
 * **The guesses are the point of the row.** Without them the picker starts at "choose one of every
 * project in the organisation", which is a worse question than the one the classifier already
 * answered badly — it thought the file was one of these, and a person recognises the right one at a
 * glance. A file the classifier could say nothing about falls back to the picker, which is also
 * every file until the classifier can read one.
 *
 * All three live in one menu, and the trigger carries the label rather than an ellipsis, because a
 * guess is named after a project and two project names do not fit an actions column side by side.
 *
 * **Filing asks the destination, and the menu knows the destination only by its ids.** Filing is
 * `update` on the record the file is filed onto, and a candidate carries the parent's ids rather
 * than its row — so a guess is dropped only where the answer needs no row: a ticket, whose rule is
 * the project relationship, and a subject the caller holds no `update` on at all. A grant bound to
 * the row (one's own projects, one's own notes) leaves the guess in place and the server answers.
 * The picker is always offered, because its destination is not chosen yet.
 *
 * Deleting is the dismissal, and there is no other: `DELETE /files/:fileId` removes the row and the
 * object, and the foreign key takes the inbox entry with it. An unfiled file hangs on nothing, so
 * it is offered to its uploader and the administrator.
 *
 * **Both surfaces that list an unfiled file render this**, the inbox and the batch report, so the
 * two lists are invalidated together rather than each refreshing only itself: filing a file from
 * the report takes it out of the inbox, and a worklist that still offers it is a worklist somebody
 * works twice.
 */
export function FileRowActions({
	file,
	candidates,
	documentKind,
	onNavigate,
}: FileRowActionsProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const filingModal = useDisclosure();
	const importModal = useDisclosure();
	const confirmModal = useAsyncModal();

	/** Query */
	const capabilities = useFileCapabilities(null);
	const { citadel } = useCitadel();

	/** Functions */
	async function handleOnRefresh() {
		await qc.invalidateQueries({ predicate: invalidate(["file-inbox", "file-batches"]) });
	}

	async function handleOnFileCandidate(index: number) {
		const candidate = offeredCandidates[index];
		if (!candidate) return;
		await fileInboxEntry(file.id, candidate.collection, candidate.foreignKeyValues);
		await handleOnRefresh();
	}

	async function handleOnDelete() {
		const isConfirmed = await confirmModal.confirm();
		if (!isConfirmed) return;
		const response = await client.files[":fileId"].$delete({ param: { fileId: file.id } });
		await responseHandler(response);
		await handleOnRefresh();
	}

	// Runs before the import navigates to the editor, which is the only moment the report still
	// exists to be closed.
	async function handleOnImported() {
		await handleOnRefresh();
		onNavigate?.();
	}

	/** Computed */
	const canDelete = capabilities.canWriteFile(file);
	const offeredCandidates = candidates
		.filter((candidate) => !candidateIsDenied(citadel, candidate))
		.slice(0, OFFERED_CANDIDATES);
	// An Angebot or a Leistungsverzeichnis is the one document in this list that has somewhere to
	// go other than a record: it can be read into a calculation. The file name is asked as well as
	// the kind, because the importer has an extractor for PDF and GAEB and for nothing else — a
	// photographed Angebot is still an Angebot and still has no way in.
	const canImportOffer =
		documentKind === "offer" &&
		isOfferImportFileName(file.fileName) &&
		citadel?.can("create", "Calculation") === true;
	// Each entry is decided on its own, so the separators between them are written unconditionally
	// and the ones left with nothing on one side are dropped. What survives also answers whether the
	// menu is worth a trigger: a caller denied both the writes and the delete has nothing to open.
	const menuItems = withoutDanglingDividers([
		canImportOffer
			? {
					label: t("calculations.import_offer"),
					icon: icon("Calculator"),
					onClick: importModal.onOpen,
				}
			: null,
		"divider",
		offeredCandidates.length ? t("files.inbox_suggestions") : null,
		...offeredCandidates.map((candidate, index) => ({
			label: candidate.label,
			icon: icon("Sparkles"),
			onClick: () => handleOnFileCandidate(index),
		})),
		"divider",
		{
			label: t("files.inbox_file_it_action"),
			icon: icon("FolderInput"),
			onClick: filingModal.onOpen,
		},
		"divider",
		canDelete
			? {
					label: t("general.delete"),
					icon: icon("Trash2"),
					onClick: handleOnDelete,
					isDanger: true,
				}
			: null,
	]);

	/** Render */
	return (
		<Fragment>
			<RowActionsWrapper to={null}>
				<Menu items={() => menuItems}>
					<Button
						size="xs"
						icon={<Icon name="ChevronDown" />}
						iconPosition="right"
					>
						{t("files.inbox_pick_destination")}
					</Button>
				</Menu>
			</RowActionsWrapper>
			{filingModal.isOpen && (
				<FileFilingModal
					isOpen={filingModal.isOpen}
					onClose={filingModal.onClose}
					onComplete={handleOnRefresh}
					fileId={file.id}
					fileName={file.fileName}
				/>
			)}
			{importModal.isOpen && (
				<ImportOfferFilesModal
					isOpen={importModal.isOpen}
					onClose={importModal.onClose}
					onComplete={handleOnImported}
					fileIds={[file.id]}
				/>
			)}
			{confirmModal.isOpen && (
				<ConfirmModal
					controller={confirmModal}
					message={t("file_upload.delete_file_confirm_message")}
				/>
			)}
		</Fragment>
	);
}

/**
 * Whether filing onto this guess is refused without the destination's row being needed to say so:
 * a ticket answers to the project relationship, which the candidate's `projectId` carries, and any
 * other parent is refused outright when the caller holds no `update` on its subject at all — the
 * first question the server asks too. Every other case is left to the server.
 */
function candidateIsDenied(
	citadel: Citadel | undefined,
	candidate: FileClassificationCandidate,
): boolean {
	if (!citadel) return false;
	const subject = FILE_PARENT_SUBJECTS[candidate.collection];
	if (subject === "Ticket") {
		const { ticketId, projectId } = candidate.foreignKeyValues;
		if (!ticketId || !projectId) return false;
		return fileWriteIsDeniedFor(citadel, { ticket: { id: ticketId, projectId } });
	}
	return citadel.cannot("update", subject);
}
